import { Modal, Button, Table } from "react-bootstrap";
import { useState, useEffect, useCallback, useRef } from "react";
import currency from "currency.js";
import { confirmable, createConfirmation } from "react-confirm";
import Select from "react-select";
import * as yup from "yup";

import "../../assets/scss/scoped/issueItem.scss";
import { fetchActionsUtil, formatForQty } from "../../utils/helpers";
import ModalLoader from "../utils/ModalLoader";
import NewRequisitionModal from "../Requisition/NewRequisition";
import { useBackendUrl } from "../../utils/hooks";
import ConfirmDialog from "../ConfirmDialogue";
import { backendApis } from "../../config";
import { useFormik } from "formik";
import { CustomInput } from "../utils/CustomInput";

const issuedToData = [
  {
    value: "",
    label: "Select Issue To",
  },
  ...backendApis.map((el) => ({
    value: el.name,
    label: el.name,
  })),
];

const issuedTypes = [
  {
    value: "",
    label: "Select Issue Type",
  },
  {
    value: "Plant",
    label: "Plant",
  },
  {
    value: "Warehouse",
    label: "Warehouse",
  },
  {
    value: "Sales",
    label: "Sales Inventory",
  },
];

function ShowProjectedItemsModal({ proceed, show, mainData }) {
  const [activeTableIndex, setActiveTableIndex] = useState(null);
  const [selectedItem, setSelectedItem] = useState({});
  const [itemsRequested, setItemsRequested] = useState([]);
  const [showNewRequisitionModal, setShowNewRequisitionModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  //=====================
  const [allStaff, setAllStaff] = useState([]);
  const [allWarehouse, setAllWarehouse] = useState([]);
  const [plantStorages, setPlantStorages] = useState([]);
  // ====================

  const backendUrl = useBackendUrl();

  const handleInputChange = ({ index, name, value }) => {
    const { prod_profiles } = selectedItem;
    const oldTableData = prod_profiles;
    oldTableData[index][name] = value;
    setSelectedItem((item) => ({ ...item, prod_profiles: [...oldTableData] }));
  };

  const [isLoadingAddToCart, setIsLoadingAddToCart] = useState(false);

  const plantLocal = JSON.parse(window.localStorage.getItem("plantDetails"));

  const fetchBatches = useCallback(
    async (items) => {
      setIsLoadingAddToCart(true);
      try {
        const data = await fetchActionsUtil(
          `${backendUrl}/api/production/get-production-batch`,
          "POST",
          "",
          { items, Plant: plantLocal.PLID },
        );
        return data?.batches || [];
      } catch (error) {
        console.log(error);
      } finally {
        setIsLoadingAddToCart(false);
      }
    },
    [backendUrl, plantLocal.PLID],
  );

  const formik = useFormik({
    initialValues: {
      Warehouse: "",
      WarehouseName: "",
      issueTo: "",
      Requester: "",
      receivingofficer: "",
      RequesterName: "",
      Quantity: "",
      type: "",
      Product_Model: "",
      Item_Name: "",
    },
    validationSchema: yup.object().shape({
      Warehouse: yup.string().required("Warehouse is required"),
      issueTo: yup.string().required("Issue To is required"),
      Quantity: yup.string().required("Quantity is required"),
      receivingofficer: yup.string().required("Receiving Officer is required"),
    }),
    onSubmit: async (values) => {
      const { prod_profiles = [], ...rest } = selectedItem;

      const products = { ...rest, ...values };

      const quantity = parseInt(products?.Quantity);
      const errors = [];

      // Validate quantities
      for (const { quantity: q, actual, title } of prod_profiles) {
        const estimated = parseInt(q) * quantity;
        const actualVal = parseInt(actual);

        if (estimated > actualVal) {
          errors.push({
            type: `${title} Quantity`,
            message: "The estimated quantity is more than the actual quantity",
          });
        } else if (actualVal > estimated) {
          errors.push({
            type: `${title} Quantity`,
            message: "The actual quantity is more than the estimated quantity",
          });
        }
      }

      // Confirm dialog if there are validation errors
      if (errors.length > 0) {
        const confirmed = await ConfirmDialog({
          title: `${errors.length} Error(s) Found`,
          description: `${errors
            ?.map((error) => `${error.type}: ${error.message}`)
            .join("\n")}\nAre you sure you want to continue?`,
        });

        if (!confirmed) return;
      }

      // Calculate total amount and proceed
      const totalAmount = prod_profiles.reduce(
        (sum, { actual, UnitCost }) =>
          sum + parseInt(actual) * parseFloat(UnitCost),
        0,
      );

      const unit = Number((parseFloat(totalAmount) / quantity).toFixed(2));

      const UnitCost = unit;
      const UnitPrice = unit;

      proceed({ ...products, UnitCost, UnitPrice, totalAmount, prod_profiles });
    },
  });

  const getStaffs = async (name) => {
    const sendTo = `${
      backendApis.find((el) => el.name === name)?.url
    }/api/users`;
    const staffs = await fetchActionsUtil(sendTo, "GET");
    staffs.staff = staffs.staff.map((el) => ({
      value: `${el.Name}-${el.email}`,
      label: el.Name,
    }));
    setAllStaff(staffs.staff);
  };

  const getWarehouse = async (name) => {
    let sendTo = "";

    const sendToUrl = `${
      backendApis.find((el) => el.name === name)?.url
    }/api/warehouse`;

    if (sendToUrl) {
      sendTo = sendToUrl || "";
      const { warehouses = [] } = await fetchActionsUtil(sendTo, "GET");
      const ware = [
        {
          value: "",
          label: "Select Warehouse",
        },

        ...warehouses?.map((el) => ({
          value: el?.W_ID,
          label: el?.W_name,
        })),
      ];
      setAllWarehouse(ware);
    }
  };

  const getPlant = async (name) => {
    let sendTo = "";

    const sendToUrl = `${
      backendApis.find((el) => el.name === name)?.url
    }/api/production/plants`;

    if (sendToUrl) {
      sendTo = sendToUrl || "";
      const { allPlants = [] } = await fetchActionsUtil(sendTo, "GET");

      const storages = allPlants?.flat()?.map((el) => ({
        [el?.Plant_Name]: el?.storageBin || [],
      }));

      const ware = [
        {
          value: "",
          label: "Select Plant",
        },
        ...allPlants?.flat()?.map((el) => ({
          value: el?.Plant_ID,
          label: el.Plant_Name,
        })),
      ];
      setPlantStorages(storages);
      setAllWarehouse(ware);
    }
  };

  // ─── Helper: sync a field to both formik and table ───────────────────────────

  const syncFields = useCallback(
    (fields) => {
      Object.entries(fields).forEach(([field, value]) => {
        formik.setFieldValue(field, value);
      });
    },
    [formik],
  );

  // ─── Stable ref for syncFields ────────────────────────────────────────────────
  const syncFieldsRef = useRef(syncFields);
  useEffect(() => {
    syncFieldsRef.current = syncFields;
  }, [syncFields]);

  useEffect(() => {
    const organize = async () => {
      const { prod_profiles, ...rest } = mainData;

      syncFieldsRef.current({
        Warehouse: rest?.Warehouse || "",
        WarehouseName: rest?.WarehouseName || "",
        issueTo: rest?.issueTo || "",
        Requester: rest?.Requester || "",
        receivingofficer: rest?.receivingofficer || "",
        RequesterName: rest?.RequesterName || "",
        Quantity: rest?.Quantity || 1,
        type: rest?.type || "",
        Product_Model: rest?.Product_Model || "",
        Item_Name: rest?.Item_Name || "",
      });

      const typeActions = {
        Plant: () => getPlant(rest?.issueTo),
        Warehouse: () => {
          getWarehouse(rest?.issueTo);
          getStaffs(rest?.issueTo);
        },
      };

      if (rest?.issueTo) {
        if (typeActions[(rest?.type)]) {
          typeActions[(rest?.type)]();
        } else {
          getStaffs(rest?.issueTo);
          setAllWarehouse([
            {
              value: "Sales",
              label: "Sales Inventory",
            },
          ]);
        }
      }

      const data = await fetchBatches(prod_profiles);

      const newProfiles = data?.map((el) => ({
        ...el,
        actual: el?.actual || el?.quantity * mainData.Quantity,
        damage: el?.damage || 0,
      }));

      // console.log({ data, newProfiles });

      setSelectedItem({ prod_profiles: newProfiles, ...rest });
    };

    organize();
  }, [mainData, fetchBatches]);

  const values = formik.values;

  useEffect(() => {
    if (values?.type === "Plant") {
      const found =
        plantStorages?.find((item) => item[(values?.WarehouseName)])?.[
          values?.WarehouseName
        ] ?? [];
      const storages = found.map((el) => ({
        value: el.Storage_ID,
        label: el.Storage_Name,
      }));
      setAllStaff([{ value: "", label: "Select Storage" }, ...storages]);
    }
  }, [values?.WarehouseName, values?.type, plantStorages]);

  const addToTable = async (e) => {
    if (e.key === "Enter") {
      const { prod_profiles, ...rest } = selectedItem;

      const newPro = prod_profiles?.map((el) => ({
        ...el,
        quantity: parseFloat(el?.actual) + parseFloat(el?.damage),
        remark: el?.remark || "",
      }));

      const newitems = { ...rest, prod_profiles: newPro };

      try {
        const { message } = await fetchActionsUtil(
          `${backendUrl}/api/production/check-quantity-items/${
            plantLocal.PLID
          }`,
          "POST",
          "",
          newitems,
        );

        setIsLoadingAddToCart(false);
        if (message.length > 0) {
          setItemsRequested(message?.map((er) => er.item));
          return setErrorMessage("Item Required not enough");
        } else {
          setErrorMessage(null);
          setItemsRequested([]);
        }
      } catch (error) {
        console.log(error);
        return setIsLoadingAddToCart(false);
      }

      setSelectedItem((item) => ({ ...item, prod_profiles }));
    }
  };

  const multiplyQuantity = (quantity) => {
    const { prod_profiles } = selectedItem;

    const newTableData = prod_profiles?.map((el) => ({
      ...el,
      actual: parseInt(el.quantity) * parseInt(quantity),
    }));

    setSelectedItem((item) => ({ ...item, prod_profiles: newTableData }));
  };

  // ─── Handlers ────────────────────────────────────────────────────────────────
  const onChangeLocation = useCallback(
    ({ value, label }) => {
      if (formik.values.type === "Plant") {
        const found = plantStorages?.find((item) => item[label])?.[label] ?? [];
        const storages = found.map((el) => ({
          value: el.Storage_ID,
          label: el.Storage_Name,
        }));
        setAllStaff([{ value: "", label: "Select Storage" }, ...storages]);
      } else {
        getStaffs(formik.values.issueTo);
      }

      syncFields({
        Warehouse: value,
        WarehouseName: label,
        Requester: value,
        receivingofficer: "",
        RequesterName: "",
      });
    },
    [formik.values.type, formik.values.issueTo, plantStorages, syncFields],
  );

  const onIssueTo = useCallback(
    (value) => {
      syncFields({ issueTo: value });

      const typeActions = {
        Plant: () => getPlant(value),
        Warehouse: () => getWarehouse(value),
      };

      if (typeActions[formik.values.type]) {
        typeActions[formik.values.type]();
      } else {
        getStaffs(value);
      }
    },
    [formik.values.type, syncFields],
  );

  const onSelectType = useCallback(
    (value) => {
      const emptyFields = {
        ...formik.values,
        receivingofficer: "",
        RequesterName: "",
        issueTo: "",
        Warehouse: "",
        WarehouseName: "",
        Requester: "",
      };

      formik.resetForm(emptyFields);
      syncFields({ ...emptyFields, type: value });
    },
    [formik, syncFields],
  );

  return (
    <Modal
      onHide={() => proceed(false)}
      show={show}
      backdropClassName={`global-backdrop`}
      aria-labelledby="contained-modal-title-vcenter"
      centered
      dialogClassName="my-style-modal"
      animation={false}
      enforceFocus={false}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <div>
            <h1> Raw Materials</h1>
          </div>

          <p>Edit each raw material quantity by changing the input.</p>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div>
          <div className="container">
            <div>
              <div>
                <div className="mb-3">
                  <div className="d-flex align-items-end justify-content-center gap-4 p-3 rounded">
                    <div className="text-nowrap">
                      <div className="text-secondary fw-medium mb-1">
                        Product name
                      </div>
                      <div className="fs-4 fw-bold">
                        {formik.values.Item_Name}
                      </div>
                    </div>

                    <div className="">
                      <CustomInput
                        name="Quantity"
                        placeholder="Enter Quantity"
                        typeOfInput="text"
                        label="Quantity"
                        value={formik.values?.Quantity}
                        onChange={(e) => {
                          formik.handleChange(e);
                          multiplyQuantity(e.target.value);
                        }}
                      />

                      {formik.touched?.Quantity && formik.errors?.Quantity && (
                        <span className="custom-invalid-feedback">
                          {formik.errors.Quantity}
                        </span>
                      )}
                    </div>

                    <div className="text-nowrap text-end">
                      <div className="text-secondary fw-medium mb-1">UOM</div>
                      <div className="fs-4 fw-bold">
                        {formik.values.Product_Model}
                      </div>
                    </div>
                  </div>
                </div>

                {/*  */}
                {/* Table */}

                <div className="mb-4">
                  <Table
                    responsive
                    borderless
                    striped
                    className="product-table"
                  >
                    <thead>
                      <tr>
                        <th>S/N</th>
                        <th>Bar Code</th>
                        <th>Name</th>
                        <th>Estimated Qty</th>
                        <th>Actual Qty</th>
                        <th>Damages</th>
                        <th>UOM</th>
                        <th>Unit Cost</th>
                        <th>Sub Total</th>
                        <th>Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItem?.prod_profiles?.length > 0 &&
                        selectedItem?.prod_profiles?.map((el, index) => (
                          <tr
                            key={index}
                            className={`${activeTableIndex === index &&
                              "active-row"}`}
                            onClick={() =>
                              activeTableIndex !== index &&
                              setActiveTableIndex(index)
                            }
                          >
                            <td>{index + 1}</td>
                            <td>{el.item}</td>
                            <td>{el.title}</td>
                            <td className="text-center">
                              {currency(el.quantity || 0, {
                                symbol: "",
                                format: formatForQty,
                              })
                                .multiply(formik.values?.Quantity || 0)
                                .format()}
                            </td>
                            <td>
                              <input
                                className="border-0 text-center w-100 form-control"
                                type="number"
                                value={el?.actual}
                                onKeyDown={(e) => addToTable(e)}
                                name="actual"
                                onChange={(e) =>
                                  handleInputChange({
                                    index,
                                    name: e.target.name,
                                    value: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>
                              <input
                                className="border-0 text-center w-100 form-control"
                                type="number"
                                value={el.damage}
                                onKeyDown={(e) => addToTable(e)}
                                name="damage"
                                onChange={(e) =>
                                  handleInputChange({
                                    index,
                                    name: e.target.name,
                                    value: e.target.value,
                                  })
                                }
                              />
                            </td>
                            <td>{el?.Product_Model || ""}</td>
                            <td>
                              {currency(parseFloat(el?.UnitCost || 1), {
                                symbol: "",
                              }).format()}
                            </td>
                            <td>
                              {currency(
                                parseFloat(el?.UnitCost || 1) *
                                  parseFloat(el?.actual || 1),
                                {
                                  symbol: "",
                                },
                              ).format()}
                            </td>
                            <td>
                              <div>
                                <textarea
                                  className="form-control"
                                  name="remark"
                                  cols={20}
                                  rows={2}
                                  onChange={(e) =>
                                    handleInputChange({
                                      index,
                                      name: e.target.name,
                                      value: e.target.value,
                                    })
                                  }
                                />
                              </div>
                            </td>
                          </tr>
                        ))}
                    </tbody>
                  </Table>
                </div>

                {/*  */}

                <div className="row">
                  <div className="col-3 mb-3">
                    <label className="form-label mb-2">Issuing Type</label>
                    <Select
                      classNamePrefix={"form-select"}
                      isSearchable={true}
                      name="type"
                      value={issuedTypes.find(
                        (el) => el.value === formik.values.type,
                      )}
                      onChange={({ value }) => {
                        onSelectType(value);
                      }}
                      options={issuedTypes}
                    />

                    {formik.touched.type && !!formik.errors.type ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.type}
                      </span>
                    ) : null}
                  </div>

                  <div className="col-3 mb-3">
                    <label className="form-label mb-2">Branch </label>
                    <Select
                      classNamePrefix={"form-select"}
                      isSearchable={true}
                      value={issuedToData.find(
                        (el) => el.value === formik.values.issueTo,
                      )}
                      onChange={({ value }) => {
                        onIssueTo(value);
                      }}
                      options={issuedToData}
                      isDisabled={formik.values.type === ""}
                    />

                    {formik.touched.issueTo && !!formik.errors.issueTo ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.issueTo}
                      </span>
                    ) : null}
                  </div>

                  <div className="col-3 mb-3">
                    <label className="form-label mb-2">
                      {formik.values.type || ""}{" "}
                    </label>
                    <Select
                      classNamePrefix={"form-select"}
                      isSearchable={true}
                      value={allWarehouse.find(
                        (el) => el.value === formik.values.Warehouse,
                      )}
                      onChange={({ value, label }) => {
                        onChangeLocation({ value, label });
                      }}
                      options={allWarehouse}
                      isDisabled={formik.values.type === "Sales"}
                    />

                    {formik.touched.Warehouse && !!formik.errors.Warehouse ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.Warehouse}
                      </span>
                    ) : null}
                  </div>

                  <div className="col-3 mb-3">
                    <label className="form-label mb-2">
                      {formik.values.type === "Plant" ? "Storage" : "Staff"}{" "}
                    </label>
                    <Select
                      classNamePrefix={"form-select"}
                      isSearchable={true}
                      value={allStaff.find(
                        (el) => el.value === formik.values.receivingofficer,
                      )}
                      onChange={({ value }) => {
                        formik.setFieldValue("receivingofficer", value);
                        formik.setFieldValue("RequesterName", value);
                      }}
                      options={allStaff}
                    />

                    {formik.touched.receivingofficer &&
                    !!formik.errors.receivingofficer ? (
                      <span className="custom-invalid-feedback">
                        {formik.errors.receivingofficer}
                      </span>
                    ) : null}
                  </div>
                </div>

                {/*  */}

                {errorMessage && (
                  <div className="d-flex justify-content-center">
                    <p className="text-danger">
                      <b>{errorMessage}</b>
                    </p>
                  </div>
                )}
                <div className="d-flex justify-content-end mt-3">
                  <Button
                    onClick={() => proceed(false)}
                    variant="white"
                    className="border bg-white mx-4"
                  >
                    Cancel
                  </Button>
                  {!errorMessage && (
                    <Button
                      onClick={formik.handleSubmit}
                      variant="primary"
                      autoFocus={true}
                    >
                      Save Actual
                    </Button>
                  )}
                </div>
              </div>
            </div>
            <ModalLoader show={isLoadingAddToCart} />

            {showNewRequisitionModal && (
              <NewRequisitionModal
                modalTitle="Send for Approval"
                defaultFormValues={{
                  title: ``,
                  description: "",
                  type: "Material Request",
                }}
                itemsRequested={itemsRequested}
                setShowNewRequisitionModal={setShowNewRequisitionModal}
              />
            )}

            {/*  */}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}

export default function ShowProjectedItemsDialog(props) {
  return createConfirmation(confirmable(ShowProjectedItemsModal))(props);
}
